import React, { FunctionComponent, useCallback, useState, memo } from "react";
import { Box, Collapse, Grid, Theme, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTheme } from "@mui/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAppDispatch } from "@app/hooks";
import { RoundedIcon } from "@shared/components/RoundedIcon";
import { ReactComponent as MinusIcon } from "@assets/svg/minus.svg";
import { ReactComponent as PlusIcon } from "@assets/svg/plus.svg";
import { openDataRoomSetupDialog } from "@dataRoomSetup/store/ducks";
import { TileProps } from "./models";

const useStyles = makeStyles<Theme, Pick<TileProps, "expandable"> & { active: boolean }>((theme) => ({
  helpText: {
    alignItems: "center",
    display: "flex",
    whiteSpace: "nowrap",
    flexWrap: "wrap",
    justifyContent: "center",
    fontSize: 14,
    paddingTop: theme.spacing(5),
    color: theme.palette.grey[800],
  },
  tileTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    transition: "all ease-in .15s",
    marginBottom: ({ active, expandable }) => theme.spacing(!expandable || active ? 5 : 0),
    "&:hover": {
      cursor: ({ expandable }) => (expandable ? "pointer" : "auto"),
      "& span": {
        color: ({ expandable }) => (expandable ? theme.palette.info.main : "auto"),
      },
      "& > div > div:nth-of-type(3) > div:first-of-type": {
        backgroundColor: theme.palette.info.main,
      },
    },
  },
  titleText: {
    textTransform: "uppercase",
    width: "100%",
    transition: "all ease-in .15s",
  },
  controlBtn: {
    color: theme.palette.info.main,
    textDecoration: "underline",
  },
}));

const DataRoomSetupTile: FunctionComponent<TileProps> = ({
  children,
  title,
  expandable = false,
  open = true,
  uncontrolled = true,
  expanded = false,
  onToggle,
  footnote,
  testId,
}) => {
  const [expandedInternal, setExpandedInternal] = useState(false);

  const onClick = useCallback(
    (event) => {
      if (event.target.textContent === "Edit" || event.target.textContent === "Add Sub Folder") return;
      if (!expandable) {
        return;
      }

      if (uncontrolled) {
        setExpandedInternal((value) => !value);
      } else {
        onToggle?.(!expanded);
      }
    },
    [uncontrolled, expanded, expandable, onToggle]
  );

  const active = uncontrolled ? expandedInternal : expanded;

  const classes = useStyles({ expandable, active });
  const dispatch = useAppDispatch();

  const ExpandIcon = active ? MinusIcon : PlusIcon;
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const handleEditClick = useCallback(() => {
    //   event.preventDefault();

    dispatch(openDataRoomSetupDialog);
  }, [dispatch]);
  const handleAddFolderClick = useCallback(() => {
    console.log("add folder");
  }, []);

  return (
    <Collapse in={open}>
      <Box
        bgcolor="background.paper"
        sx={{
          width: 1,
          padding: theme.spacing(10),
          [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(7.5),
          },
        }}
      >
        {title && (
          <Grid container className={classes.tileTitle} onClick={onClick}>
            <Grid item>
              <Box>
                <Typography
                  variant={isSmall ? "subhead3" : "subhead2"}
                  className={classes.titleText}
                  data-testid={testId ? `reporting-tile-title-${testId}` : "reporting-tile-title"}
                >
                  {title}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Grid container columnGap={10} className={classes.controlBtn}>
                <Grid item>
                  <Box onClick={handleEditClick}>Edit</Box>
                </Grid>
                <Grid item>
                  <Box onClick={handleAddFolderClick}>Add Sub Folder</Box>
                </Grid>
                <Grid item>
                  <Box id="expendTile">
                    {expandable && <RoundedIcon Icon={ExpandIcon} interactive testId={testId} />}{" "}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Collapse in={expandable ? active : true} data-testid={testId ? `tile-children-${testId}` : "tile-children"}>
          {children}
        </Collapse>
        <Collapse in={!!footnote}>
          <Box mt={15} fontSize={16} className={classes.helpText}>
            {footnote}
          </Box>
        </Collapse>
      </Box>
    </Collapse>
  );
};

export default memo(DataRoomSetupTile);
